import React, { useMemo } from 'react';
import areaPositions from '../areaPositions';
import IslandMap from './IslandMap';
import { MY_TEAM_INDEX } from './liveAssistState';
import { splitMappableGroups, tallyDestinations } from './routeDestinations';

// 지도 레이어 "예상 도착 지역": 24명이 가장 많이 쓰는 루트의 마지막 도착 지역을 지도 위에 인원 수로 표시한다.
// 지도에 올릴 수 없는 항목(알 수 없음, 조회 실패 등)은 지도 아래에 따로 보여준다. 인라인 스타일만 쓴다(별도 창이라 CSS가 없음).

const MUTED = '#9aa0a6';
const MINE = '#52c41a'; // 우리 팀 표시색(PipTeamsView와 같음)
const BASE = '#378ADD';

const memberTitle = (g) => g.members
  .map((m) => `${m.teamIndex === MY_TEAM_INDEX ? '우리 팀' : `${m.teamIndex + 1}팀`} ${m.characterName}`)
  .join(', ');

function MemberFace({ member }) {
  const mine = member.teamIndex === MY_TEAM_INDEX;
  return (
    <img
      src={`/img/character/${member.characterName}.png`}
      alt={member.characterName}
      title={`${mine ? '우리 팀' : `${member.teamIndex + 1}팀`} · ${member.characterName}`}
      style={{
        width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', background: '#3a3a4d',
        boxSizing: 'border-box', border: `2px solid ${mine ? MINE : 'transparent'}`,
      }}
      onError={(e) => { e.target.style.visibility = 'hidden'; }}
    />
  );
}

// 지역 마커: 지도 위 지역 위치에 인원 수 원. 우리 팀이 있으면 초록 테두리와 작은 배지로 우리 인원을 보여준다.
function AreaMarker({ group, maxCount }) {
  const pos = areaPositions[group.area];
  const size = 24 + Math.round((group.count / maxCount) * 14);
  const hasMine = group.myCount > 0;
  return (
    <div
      title={`${group.area} ${group.count}명 — ${memberTitle(group)}`}
      style={{
        position: 'absolute', left: `${pos.left}%`, top: `${pos.top}%`, transform: 'translate(-50%, -50%)',
        width: size, height: size, borderRadius: '50%', background: BASE, color: '#fff',
        border: `2px solid ${hasMine ? MINE : '#fff'}`, boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 800, boxShadow: '0 2px 4px rgba(0,0,0,0.5)', zIndex: 2,
      }}
    >
      {group.count}
      {hasMine && (
        <span
          style={{
            position: 'absolute', right: -6, top: -6, minWidth: 15, height: 15, borderRadius: 8, background: MINE,
            color: '#fff', fontSize: 10, lineHeight: '15px', textAlign: 'center', padding: '0 2px', boxSizing: 'border-box',
          }}
        >
          {group.myCount}
        </span>
      )}
    </div>
  );
}

export default function PipDestinationsView({ entries }) {
  const { groups, total, pending } = useMemo(() => tallyDestinations(entries, MY_TEAM_INDEX), [entries]);
  const { onMap, offMap } = useMemo(() => splitMappableGroups(groups, areaPositions), [groups]);
  const maxCount = Math.max(1, ...onMap.map((g) => g.count));
  const highlighted = useMemo(() => new Set(onMap.map((g) => g.area)), [onMap]);

  return (
    <div>
      <div style={{ color: MUTED, marginBottom: 6, lineHeight: '16px' }}>
        각자 가장 많이 쓰는 루트의 마지막 지역 · {total - pending}/{total}명 확인{pending > 0 ? ' (조회 중…)' : ''}
      </div>
      <IslandMap highlighted={highlighted}>
        {onMap.map((g) => <AreaMarker key={g.area} group={g} maxCount={maxCount} />)}
      </IslandMap>
      <div style={{ color: MUTED, margin: '4px 0 6px', lineHeight: '16px' }}>
        숫자는 인원 · <span style={{ color: MINE }}>초록 테두리/배지</span>는 우리 팀 포함(우리 인원)
      </div>
      {offMap.length > 0 && (
        <div style={{ padding: '4px 6px', background: '#2a2a3d', borderRadius: 4 }}>
          <div style={{ color: MUTED, marginBottom: 4 }}>지도에 표시할 수 없음</div>
          {offMap.map((g) => (
            <div key={g.area} style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>{g.area}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{g.count}명</span>
                {g.myCount > 0 && <span style={{ color: MINE }}>(우리 {g.myCount})</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 2 }}>
                {g.members.map((m) => <MemberFace key={`${m.teamIndex}-${m.characterName}`} member={m} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
